import React from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useOrderDetailsFilterQuery } from "@queries/order";
import { useTimeSlotsQuery } from "@queries/time_slots";
import "@styles/opencart-admin.css";

const ORDER_TYPES = ["express","car_cleaning","normal","skip","complementary","disinfection","furniture","housekeeping","carpets","chemicals"];

const normalizeDay = (s) => {
  if (!s) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
    const [d, m, y] = s.split("/").map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    if (!Number.isNaN(dt.getTime())) return dt.toISOString().slice(0, 10);
  }
  return null;
};
const todayISO = () => new Date().toISOString().slice(0, 10);

const fromSearch = (sp) => ({
  day: normalizeDay(sp.get("day")) || todayISO(),
  time_slot: sp.get("time_slot") || "",
  order_type: sp.get("order_type") || "carpets", // default selected
});

const toSearch = (f) => {
  const sp = new URLSearchParams();
  sp.set("day", f.day);
  if (f.time_slot) sp.set("time_slot", f.time_slot);
  if (f.order_type) sp.set("order_type", f.order_type);
  return sp;
};

export default function CarpetsCleaningOrdersByTwoDays() {
  const [search, setSearch] = useSearchParams();
  const [filters, setFilters] = React.useState(fromSearch(search));
  React.useEffect(() => setFilters(fromSearch(search)), [search]);

  const day = normalizeDay(filters.day) || todayISO();
  const addDaysISO = (iso, n) => {
    const d = new Date(iso + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
  };
  const day2 = React.useMemo(() => addDaysISO(day, 1), [day]);

  const { data: timeSlots = [], isLoading: tsLoading } = useTimeSlotsQuery(day);

  const q1 = React.useMemo(
    () => ({
      from_date: day,
      to_date: day,
      time_slot: filters.time_slot || undefined,
      order_type: filters.order_type || undefined,
      with_items: 1,
      order_status_id: 17,
      page: 1,
      page_size: 1000,
    }),
    [day, filters.time_slot, filters.order_type]
  );

  const q2 = React.useMemo(
    () => ({
      from_date: day2,
      to_date: day2,
      // no time_slot for day+1
      order_type: filters.order_type || undefined,
      with_items: 1,
      order_status_id: 17,
      page: 1,
      page_size: 1000,
    }),
    [day2, filters.order_type]
  );

  const { data: resp1, isLoading: l1, isFetching: f1 } = useOrderDetailsFilterQuery(q1);
  const { data: resp2, isLoading: l2, isFetching: f2 } = useOrderDetailsFilterQuery(q2);

  const rows1 = resp1?.data ?? [];
  const rows2 = resp2?.data ?? [];

  const apply = () => setSearch(toSearch({ ...filters, day }), { replace: true });

  return (
    <div className="oc-content">
      {(l1 || f1 || l2 || f2) && <div className="oc-card">Loading…</div>}

      <div className="oc-header">
        <h1>Cleaning Orders (Two Days)</h1>
        <nav className="oc-bc"><span>Home</span><span className="sep">›</span><span>Cleaning Orders</span></nav>
      </div>

      <div className="oc-card oc-filter">
        <div className="title">Filter</div>
        <div style={{ display: "grid", gridTemplateColumns: "220px 240px 220px 120px auto", gap: 12, alignItems: "end" }}>
          <div>
            <label className="lbl">Delivery Day</label>
            <input type="date" value={day} onChange={(e) => setFilters((f) => ({ ...f, day: e.target.value }))} className="oc-input" />
          </div>
          <div>
            <label className="lbl">Order Type</label>
            <select
              value={filters.order_type}
              onChange={(e) => setFilters((f) => ({ ...f, order_type: e.target.value }))}
              className="oc-input"
            >
              {/* include "All" option if user wants to see all types */}
              <option value="">All types</option>
              {ORDER_TYPES.map((t) => (
                <option key={t} value={t}>{labelForType(t)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="lbl">Time Slot</label>
            <select
              value={filters.time_slot}
              onChange={(e) => setFilters((f) => ({ ...f, time_slot: e.target.value }))}
              className="oc-input"
              disabled={tsLoading}
            >
              <option value="">All</option>
              {timeSlots.map((t) => {
                const value = t?.value ?? t;
                const label = t?.label ?? t;
                return <option key={value} value={value}>{label}</option>;
              })}
            </select>
          </div>
          <div><button className="oc-btn" onClick={apply}>Filter</button></div>
        </div>
      </div>

      <DaySection title={`Order List - Date: ${day}`} rows={rows1} />
      <DaySection title={`Order List - Date: ${day2}`} rows={rows2} />
    </div>
  );
}

function DaySection({ title, rows }) {
  const grouped = React.useMemo(() => {
    const g = {};
    for (const r of rows) {
      const t = r.type || "unknown";
      if (!g[t]) g[t] = [];
      g[t].push(r);
    }
    return g;
  }, [rows]);

  const typesInOrder = React.useMemo(() => {
    const present = Object.keys(grouped);
    return ["express", ...present.filter((t) => t !== "express").sort((a, b) => a.localeCompare(b))];
  }, [grouped]);

  return (
    <div className="oc-card" style={{ marginTop: 16 }}>
      <div className="oc-toolbar"><strong>{title}</strong></div>
      {typesInOrder.filter((t) => grouped[t]?.length).map((t) => (
        <div key={t} style={{ marginTop: 8 }}>
          <div style={{ fontWeight: 600, margin: "8px 0" }}>{labelForType(t)}</div>
          <OrdersTable rows={grouped[t]} />
        </div>
      ))}
      {!rows.length && <div style={{ padding: 12 }}>No results!</div>}
    </div>
  );
}

function OrdersTable({ rows }) {
  return (
    <div className="table-wrap" style={{ overflowX: "auto" }}>
      <table className="notes-table" style={{ width: "100%" }}>
        <thead>
          <tr>
            <th style={{ width: 70 }}>ID</th>
            <th>Items</th>
            <th style={{ width: 220 }}>Time Slot</th>
            <th style={{ width: 220 }}>Customer</th>
            <th>Notes</th>
            <th style={{ width: 70 }}>1st</th>
            <th style={{ width: 100 }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.order_id}>
              <td>{r.order_id}</td>
              <td>
              {Array.isArray(r.items) && r.items.length ? (
                  r.items.map((item, i) => (
                      <div
                      key={i}
                      dangerouslySetInnerHTML={{ __html: item }}
                      />
                  ))
              ) : (
                  "-"
              )}
              </td>
              <td>
                {r.pickup_time_slot ? `Pickup: ${r.pickup_time_slot}` : ""}
                {r.pickup_time_slot && r.dropoff_time_slot ? <br /> : null}
                {r.dropoff_time_slot ? `Delivery: ${r.dropoff_time_slot}` : ""}
                {!r.pickup_time_slot && !r.dropoff_time_slot ? "-" : null}
              </td>
              <td>{r.customer || "-"}</td>
              <td style={{ whiteSpace: "normal" }} dangerouslySetInnerHTML={{ __html: r.notes_display || "-" }} />
              <td>{r.first_order ? "Yes" : "No"}</td>
              <td>
                <div style={{ display: "flex", gap: 6 }}>
                  <Link className="oc-btn ghost" to={`/orders/${r.order_id}`}>view</Link>
                </div>
              </td>
            </tr>
          ))}
          {!rows.length && (
            <tr><td colSpan={7} style={{ textAlign: "center", padding: 14 }}>No rows</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function labelForType(t) {
  if (t === "car_cleaning") return "Car Cleaning";
  if (t === "express") return "Express Orders";
  if (t === "complementary") return "Complementary";
  if (t === "housekeeping") return "Housekeeping";
  if (t === "carpets") return "Carpets";
  return t.charAt(0).toUpperCase() + t.slice(1);
}

